import React from 'react';
import {
    Button,
    Card,
    CardBody,
    Col,
    FormGroup,
    Input,
    Label,
    Row,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding,
    faBriefcase,
    faPlus,
    faProjectDiagram,
    faTrashAlt,
    faUser,
} from '@fortawesome/free-solid-svg-icons';
import { v4 as uuidv4 } from 'uuid';
import ReactSelect from 'react-select';

/*
 BC: Beneficiario Controlador

 Modelo de datos:
 - nivel 0 (raíz): entidad es el participante principal. Sus acciones se miden
   contra totalAccionesGlobal (total de la empresa raíz, aportado por el padre).
 - nivel > 0: entidad es un sub-nodo (dueño beneficiario en cascada, representante
   legal, apoderado o admin por control).

 Campos clave en entidad:
   acciones            → N° de acciones/participaciones que este nodo posee en su padre.
                         En sub-socios de una Moral (totalAccionesGlobal = 100) representa
                         el % directo de participación en el capital de esa empresa.
   tieneSociosMayores  → 'si' | 'no' | '' — ¿existen socios con >25% en esta Moral?
   subSocios           → array de entidades hijas (dueños beneficiarios en cascada).
   representante       → entidad hija (representante legal o apoderado).
   adminUnico          → entidad hija (administrador BC por control).

 Lógica de distribución (BC por propiedad):
   Toda entidad Moral tiene un 100% implícito de capital. Los sub-socios capturan
   directamente su % de participación (campo acciones con totalAccionesGlobal = 100).
   El botón "Añadir dueño beneficiario" aparece mientras la suma de % ya asignados
   a subSocios deje más del 25% disponible, ya que todavía puede existir otro BC
   potencial que deba declararse.

 Sincronización: todo el estado sube vía el callback `actualizar`. Los sub-nodos
 actualizan su propio slice del árbol mediante handleChange, que propaga hacia
 arriba hasta el componente raíz.
*/
export default function NodoEntidad({
    entidad,
    actualizar,
    eliminar,
    nivel = 0,
    numeroSocio,
    totalAccionesGlobal = 0,
    paises = [],
    rolesPermitidos = [],
    mostrarAccionesProp = true,
    tipoRepresentacionGlobal = 'acciones',
    // % ya tomado por hermanos / nodos existentes (excluye a esta entidad)
    porcentajeOcupadoOtros = 0,
}) {
    function handleChange(campo, valor) {
        actualizar({ ...entidad, [campo]: valor });
    }

    function nuevaEntidad(rolBase = '') {
        return { tipo: 'Fisica', acciones: 0, rolBase, id: uuidv4() };
    }

    const esFisica = entidad.tipo === 'Fisica';
    const esMoral = !esFisica;
    const esExtranjero = entidad.nacionalidad && entidad.nacionalidad !== 'México';
    const esRolPrincipal = nivel === 0;

    const esDistribucionPorcentaje = totalAccionesGlobal === 100;
    const totalNum = parseFloat(totalAccionesGlobal || 0);
    const accionesNum = parseFloat(entidad.acciones || 0);
    const porcentajeCalculado =
        totalNum > 0 && entidad.acciones
            ? ((accionesNum / totalNum) * 100).toFixed(2)
            : '0.00';
    const usaInputPorcentaje =
        (tipoRepresentacionGlobal === 'participaciones' && esRolPrincipal) ||
        esDistribucionPorcentaje;
    const maxPctDisponible = Math.max(
        0,
        100 - (parseFloat(porcentajeOcupadoOtros) || 0)
    );
    const pctParticipacionInvalido =
        usaInputPorcentaje &&
        entidad.acciones !== '' &&
        entidad.acciones !== null &&
        entidad.acciones !== undefined &&
        (Number.isNaN(accionesNum) ||
            accionesNum <= 25 ||
            accionesNum > maxPctDisponible);

    const maxLengthRFC = esExtranjero ? 50 : esFisica ? 13 : 12;
    const placeholderRFC = esExtranjero
        ? 'ID Internacional'
        : esFisica
            ? 'XXXX999999XXX'
            : 'XXX999999XXX';

    const rolBase = entidad.rolBase || '';
    const esRepresentanteFinal =
        rolBase.includes('APODERADO') || rolBase.includes('REPRESENTANTE');

    const esAdministradorBC = rolBase.includes('ADMINISTRADOR POR CONTROL (BC)');
    // Los representantes y apoderados no son accionistas; no necesitan el campo de acciones
    const mostrarAcciones = mostrarAccionesProp && !esRepresentanteFinal && !esAdministradorBC;

    const cardClass = nivel > 0
        ? 'mb-3 ml-4 shadow-sm border-left border-primary'
        : 'mb-3 shadow-sm';

    // — Distribución de capital entre sub-socios (para entidades Morales) ——————

    // Suma de acciones ya asignadas a sub-socios directos
    function getDistribuido() {
        return (entidad.subSocios || []).reduce(
            (acc, s) => acc + parseFloat(s.acciones || 0), 0
        );
    }

    // Porcentaje del capital propio aún sin asignar a ningún sub-socio (base implícita = 100%)
    function getPorcentajePendiente() {
        return 100 - getDistribuido();
    }

    // Mientras quede >25% libre puede existir otro BC → el botón debe mostrarse
    function canAddParticipante() {
        return getPorcentajePendiente() > 25;
    }

    return (
        <Card className={cardClass} style={nivel > 0 ? { borderLeftWidth: '4px' } : undefined}>
            <CardBody>

                {/* Cabecera: icono + título + botón quitar */}
                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                    <div className="d-flex align-items-center">
                        <FontAwesomeIcon
                            icon={esFisica ? faUser : faBuilding}
                            className={esFisica ? 'text-primary mr-2' : 'text-danger mr-2'}
                        />
                        <h6 className="mb-0 font-weight-bold text-uppercase">
                            {esRolPrincipal
                                ? `Cliente ${numeroSocio}`
                                : entidad.rolBase}
                        </h6>
                    </div>
                    {eliminar ? (
                        <Button
                            type="button"
                            color="link"
                            size="sm"
                            className="text-danger font-weight-bold"
                            onClick={eliminar}
                        >
                            <FontAwesomeIcon icon={faTrashAlt} className="mr-1" />
                            Quitar
                        </Button>
                    ) : null}
                </div>

                {/* Fila 1: Rol (solo nivel 0) + Tipo + Nacionalidad */}
                <Row>
                    {esRolPrincipal ? (
                        <Col md={entidad.rolBase === 'OTRO' ? '3' : '4'}>
                            <FormGroup>
                                <Label className="small text-uppercase text-muted mb-1">
                                    Rol en la operación *
                                </Label>
                                <Input
                                    type="select"
                                    value={entidad.rolBase || ''}
                                    onChange={(e) => {
                                        const nuevoRol = e.target.value;
                                        actualizar({
                                            ...entidad,
                                            rolBase: nuevoRol,
                                            // Limpiar especificación al cambiar a un rol distinto de OTRO
                                            otroEspecificacion: nuevoRol === 'OTRO'
                                                ? (entidad.otroEspecificacion || '')
                                                : '',
                                        });
                                    }}
                                    className="bg-light font-weight-bold text-primary"
                                >
                                    <option value="">Asignar rol...</option>
                                    {rolesPermitidos.map((rol) => (
                                        <option key={rol} value={rol}>{rol}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    ) : null}
                    {entidad.rolBase === 'OTRO' ? (
                        <Col>
                            <FormGroup>
                                {entidad.rolBase === 'OTRO' ? (
                                    <FormGroup>
                                        <Label className="small text-uppercase text-muted mb-1">
                                            Especificar rol *
                                        </Label>
                                        <Input
                                            type="text"
                                            value={entidad.otroEspecificacion || ''}
                                            onChange={(e) => handleChange('otroEspecificacion', e.target.value)}
                                            placeholder="Ej. Fideicomitente, Beneficiario..."
                                            className="border-primary"
                                        />
                                    </FormGroup>
                                ) : null}
                            </FormGroup>
                        </Col>
                    ) : null}

                    <Col md={esRolPrincipal ? (entidad.rolBase === 'OTRO' ? '3' : '4') : '6'}>
                        <FormGroup>
                            <Label className="small text-uppercase text-muted mb-1">
                                Tipo de persona
                            </Label>
                            <Input
                                type="select"
                                value={entidad.tipo || 'Fisica'}
                                onChange={(e) => handleChange('tipo', e.target.value)}
                            >
                                <option value="Fisica">Física</option>
                                <option value="Moral">Moral / Fideicomiso</option>
                            </Input>
                        </FormGroup>
                    </Col>

                    <Col md={esRolPrincipal ? (entidad.rolBase === 'OTRO' ? '3' : '4') : '6'}>
                        <FormGroup>
                            <Label className="small text-uppercase text-muted mb-1">
                                Nacionalidad *
                            </Label>
                            <ReactSelect
                                placeholder="Seleccionar..."
                                value={
                                    entidad.nacionalidad
                                        ? { value: entidad.nacionalidad, label: entidad.nacionalidad }
                                        : null
                                }
                                onChange={(option) =>
                                    actualizar({
                                        ...entidad,
                                        nacionalidad: option?.value || '',
                                        pldNacionalidadClave: option?.clave || '',
                                    })
                                }
                                options={paises.map((pais) => ({
                                    value: pais.nombre,
                                    label: pais.nombre,
                                    clave: pais.clave,
                                }))}
                            />
                        </FormGroup>
                    </Col>
                </Row>

                {/* Fila 2: Nombre / Apellidos / RFC / Acciones o % participación */}
                <Row>
                    <Col md={esFisica ? '4' : '8'}>
                        <FormGroup>
                            <Label className="small text-uppercase text-muted mb-1">
                                {esFisica
                                    ? 'Nombre(s) *'
                                    : 'Denominación o razón social *'}
                            </Label>
                            <Input
                                type="text"
                                value={entidad.nombre || ''}
                                onChange={(e) => handleChange('nombre', e.target.value)}
                                placeholder={esFisica ? 'Ej. Pedro' : 'Ej. Inmobiliaria S.A.'}
                            />
                        </FormGroup>
                    </Col>

                    {esFisica ? (
                        <>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="small text-uppercase text-muted mb-1">
                                        Ap. paterno *
                                    </Label>
                                    <Input
                                        type="text"
                                        value={entidad.paterno || ''}
                                        onChange={(e) => handleChange('paterno', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="small text-uppercase text-muted mb-1">
                                        Ap. materno
                                    </Label>
                                    <Input
                                        type="text"
                                        value={entidad.materno || ''}
                                        onChange={(e) => handleChange('materno', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </>
                    ) : null}

                    <Col md="4">
                        <FormGroup>
                            <Label className="small text-uppercase text-muted mb-1">
                                {esExtranjero ? 'Tax ID / NIF' : 'RFC'}
                                {entidad.tipo === 'Fisica' && (esRepresentanteFinal || esRolPrincipal) ? ' *' : ''}
                            </Label>
                            <Input
                                type="text"
                                value={entidad.rfc || ''}
                                onChange={(e) =>
                                    handleChange('rfc', e.target.value.toUpperCase())
                                }
                                maxLength={maxLengthRFC}
                                placeholder={placeholderRFC}
                                className="text-uppercase"
                                required={entidad.tipo === 'Fisica' && (esRepresentanteFinal || esRolPrincipal)}
                            />
                        </FormGroup>
                    </Col>

                    {/* Acciones en la empresa padre + % calculado automático */}
                    {mostrarAcciones && tipoRepresentacionGlobal !== 'sin-acciones' ? (
                        <Col md="4">
                            {usaInputPorcentaje ? (
                                <FormGroup>
                                    <Label className="small text-uppercase text-primary mb-1">
                                        % de participación *
                                    </Label>
                                    <Input
                                        type="number"
                                        value={entidad.acciones || ''}
                                        onChange={(e) =>
                                            handleChange('acciones', e.target.value)
                                        }
                                        placeholder="Ej. 30"
                                        min="25.0001"
                                        max={maxPctDisponible}
                                        step="any"
                                        className={`bg-light font-weight-bold${pctParticipacionInvalido && nivel > 0 ? ' is-invalid' : ''
                                            }`}
                                    />
                                    {pctParticipacionInvalido && nivel > 0 ? (
                                        <small className="text-danger">
                                            Debe ser mayor a 25% y en conjunto con los otros socios no exceder el 100%.
                                        </small>
                                    ) : null}
                                </FormGroup>
                            ) : (
                                <Row>
                                    <Col xs="6">
                                        <FormGroup>
                                            <Label className="small text-uppercase text-primary mb-1">
                                                N° acciones
                                            </Label>
                                            <Input
                                                type="number"
                                                value={entidad.acciones || ''}
                                                onChange={(e) =>
                                                    handleChange('acciones', e.target.value)
                                                }
                                                placeholder="Ingresar..."
                                                min="0"
                                                step="any"
                                                className="bg-light font-weight-bold"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col xs="6">
                                        <FormGroup>
                                            <Label className="small text-uppercase text-muted mb-1">
                                                % (auto)
                                            </Label>
                                            <Input
                                                type="text"
                                                value={`${porcentajeCalculado}%`}
                                                disabled
                                                className="bg-light font-weight-bold text-muted"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}
                        </Col>
                    ) : null}
                </Row>

                {/* Representante legal (Moral) o apoderado (Física) */}
                {!esRepresentanteFinal ? (
                    <div className="bg-light p-3 rounded border mt-2" hidden={!esRolPrincipal}>
                        {esMoral ? (
                            <>
                                <Label className="small font-weight-bold d-flex align-items-center mb-2">
                                    <FontAwesomeIcon
                                        icon={faBriefcase}
                                        className="text-muted mr-2"
                                    />
                                    Representante legal (obligatorio para PM)
                                </Label>
                                <NodoEntidad
                                    entidad={
                                        entidad.representante ||
                                        nuevaEntidad('REPRESENTANTE LEGAL / APODERADO')
                                    }
                                    actualizar={(rep) => handleChange('representante', rep)}
                                    nivel={nivel + 1}
                                    paises={paises}
                                />
                            </>
                        ) : (
                            <>
                                <Label className="small font-weight-bold d-flex align-items-center mb-2">
                                    <FontAwesomeIcon
                                        icon={faBriefcase}
                                        className="text-muted mr-2"
                                    />
                                    ¿Comparece por su propio derecho o por representante?
                                </Label>
                                <Input
                                    type="select"
                                    value={entidad.tieneRepresentante || ''}
                                    onChange={(e) =>
                                        handleChange('tieneRepresentante', e.target.value)
                                    }
                                    className="w-50 mb-3"
                                >
                                    <option value="">Seleccionar...</option>
                                    <option value="no">Por su propio derecho</option>
                                    <option value="si">
                                        A través de un representante / apoderado
                                    </option>
                                </Input>

                                {entidad.tieneRepresentante === 'si' ? (
                                    <NodoEntidad
                                        entidad={
                                            entidad.representante || nuevaEntidad('APODERADO')
                                        }
                                        actualizar={(rep) => handleChange('representante', rep)}
                                        nivel={nivel + 1}
                                        paises={paises}
                                    />
                                ) : null}
                            </>
                        )}
                    </div>
                ) : null}

                {/* Estructura corporativa PLD — aplica a toda entidad Moral en cualquier nivel */}
                {esMoral ? (
                    <div className="bg-warning-soft p-3 rounded border border-warning mt-3">
                        <Label className="small font-weight-bold text-warning d-flex align-items-center mb-2">
                            <FontAwesomeIcon icon={faProjectDiagram} className="mr-2" />
                            Estructura corporativa (PLD) de:{' '}
                            {entidad.nombre || '_________________'}
                        </Label>
                        <p className="small text-uppercase text-muted mb-2">
                            ¿Existen socios con más del 25% del capital dentro de esta empresa?
                        </p>
                        <Input
                            type="select"
                            value={entidad.tieneSociosMayores || ''}
                            onChange={(e) =>
                                handleChange('tieneSociosMayores', e.target.value)
                            }
                            className="w-50 mb-3"
                        >
                            <option value="">Seleccionar...</option>
                            <option value="si">
                                Sí
                            </option>
                            <option value="no">
                                No, el capital está pulverizado / diluido
                            </option>
                        </Input>

                        {/* Capital pulverizado: BC por control (administrador único) */}
                        {entidad.tieneSociosMayores === 'no' ? (
                            <div className="mt-2">
                                <p className="small font-weight-bold text-uppercase text-warning mb-2">
                                    Por ley, identifique al administrador / gerente por control de la empresa {entidad.nombre}:
                                </p>
                                <NodoEntidad
                                    entidad={
                                        entidad.adminUnico ||
                                        nuevaEntidad('ADMINISTRADOR POR CONTROL (BC)')
                                    }
                                    actualizar={(adm) => handleChange('adminUnico', adm)}
                                    nivel={nivel + 1}
                                    paises={paises}
                                />
                            </div>
                        ) : null}

                        {/* BC por propiedad: cascada de dueños beneficiarios */}
                        {entidad.tieneSociosMayores === 'si' ? (
                            <div className="mt-3 pt-3 border-top border-warning">
                                <h6 className="small text-uppercase text-muted mb-3">
                                    Desglose a los socios de {entidad.nombre} con más del 25% del capital
                                </h6>

                                {(entidad.subSocios || []).map((subSocio) => (
                                    <NodoEntidad
                                        key={subSocio.id}
                                        entidad={subSocio}
                                        nivel={nivel + 1}
                                        /*
                                         * Los sub-socios capturan % directo sobre el 100%
                                         * implícito del capital de esta Moral.
                                         */
                                        totalAccionesGlobal={100}
                                        porcentajeOcupadoOtros={(entidad.subSocios || [])
                                            .filter((s) => s.id !== subSocio.id)
                                            .reduce(
                                                (acc, s) =>
                                                    acc + parseFloat(s.acciones || 0),
                                                0
                                            )}
                                        paises={paises}
                                        actualizar={(mod) =>
                                            handleChange(
                                                'subSocios',
                                                (entidad.subSocios || []).map((s) =>
                                                    s.id === mod.id ? mod : s
                                                )
                                            )
                                        }
                                        eliminar={() =>
                                            handleChange(
                                                'subSocios',
                                                (entidad.subSocios || []).filter(
                                                    (s) => s.id !== subSocio.id
                                                )
                                            )
                                        }
                                    />
                                ))}

                                {canAddParticipante() ? (
                                    <Button
                                        type="button"
                                        color="danger"
                                        outline
                                        size="sm"
                                        onClick={() =>
                                            handleChange('subSocios', [
                                                ...(entidad.subSocios || []),
                                                nuevaEntidad('DUEÑO BENEFICIARIO (CASCADA)'),
                                            ])
                                        }
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="mr-1" />
                                        Añadir socio con más del 25% del capital de la empresa {entidad.nombre}
                                    </Button>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ) : null}

            </CardBody>
        </Card>
    );
}
